import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Download, Trash2, ShieldAlert, Loader2, FileArchive, X, Clock, CheckCircle2,
  AlertTriangle, KeyRound, MailCheck,
} from "lucide-react";
import { api, API } from "../lib/api";
import { useApp } from "../lib/AppContext";
import { formatDateTime } from "../lib/format";

const SCOPE_OPTIONS = [
  { value: "full", label: "Tutto (predefinito)",
    hint: "Include tutti gli ambiti sotto elencati." },
  { value: "agenda", label: "Solo agenda e calendario",
    hint: "Cicli, scadenze, eventi, obiettivi personali, ricorrenze." },
  { value: "finanze", label: "Solo finanze",
    hint: "Conti, voci, carte, abbonamenti, PAC, promemoria finanziari." },
  { value: "documenti", label: "Solo documenti (metadata + file)",
    hint: "Documenti caricati, estrazioni AI, log di accesso ai file." },
  { value: "crm", label: "Solo CRM Prime Vision",
    hint: "Lead, attività, note, stage della pipeline." },
  { value: "formazione", label: "Solo formazione ECM/congressi",
    hint: "Corsi, ECM, congressi, polizze professionali." },
  { value: "segretaria", label: "Solo dati Segretaria IA",
    hint: "Memorie, conversazioni, azioni proposte/confermate." },
  { value: "auth-privacy", label: "Solo autenticazione e privacy",
    hint: "Sessioni, audit log (12 mesi), impostazioni digest, notifiche." },
];

const STATUS_LABELS = {
  pending: { label: "In coda", color: "bg-amber-50 text-amber-800 border-amber-200" },
  processing: { label: "In elaborazione", color: "bg-blue-50 text-blue-800 border-blue-200" },
  ready: { label: "Pronto", color: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  failed: { label: "Fallito", color: "bg-red-50 text-red-800 border-red-200" },
  expired: { label: "Scaduto", color: "bg-neutral-100 text-neutral-600 border-neutral-200" },
};

function humanBytes(n) {
  if (!n) return "—";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0; let v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${u[i]}`;
}

async function ensureStepup(password) {
  const res = await api.stepupPassword(password);
  window.__pxos_stepup = res.stepup_token;
  return res.stepup_token;
}

// ═════════════════════════════════════════════════════════════
// Export section
// ═════════════════════════════════════════════════════════════
function ExportSection() {
  const [scope, setScope] = useState("full");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [pwd, setPwd] = useState("");

  const load = async () => {
    setLoading(true);
    try { setItems(await api.gdprListExports()); } catch { toast.error("Impossibile caricare gli export"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    // Poll pending/processing every 5s while any is in-flight
    const inflight = items.some(x => x.status === "pending" || x.status === "processing");
    if (!inflight) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [items]);

  const request = async () => {
    if (!pwd) { toast.error("Inserisci la password per L2"); return; }
    setRequesting(true);
    try {
      await ensureStepup(pwd);
      await api.gdprCreateExport(scope);
      toast.success("Richiesta export inviata · riceverai una notifica quando è pronto");
      setPwd("");
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Errore nella richiesta";
      toast.error(detail);
    } finally { setRequesting(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo export? Il file verrà rimosso definitivamente.")) return;
    try { await api.gdprDeleteExport(id); toast.success("Export eliminato"); load(); }
    catch { toast.error("Errore eliminazione"); }
  };

  const selected = SCOPE_OPTIONS.find(s => s.value === scope);

  return (
    <div className="pxos-card p-5 sm:p-6 space-y-5" data-testid="privacy-export-section">
      <div className="flex items-start gap-3">
        <FileArchive size={22} className="mt-1 opacity-80" />
        <div>
          <h2 className="font-display text-xl font-semibold" data-testid="privacy-export-heading">
            Esporta i miei dati
          </h2>
          <p className="text-sm opacity-75 mt-1">
            Genera un archivio <strong>ZIP</strong> con i tuoi dati. Ogni export
            è disponibile per 7 giorni ed è protetto da download token.
            Limite: <strong>3 export al mese</strong>.
          </p>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-[1fr_auto] items-end">
        <div>
          <label className="text-xs font-medium opacity-80">Ambito dell'export</label>
          <select
            value={scope} onChange={(e) => setScope(e.target.value)}
            data-testid="privacy-export-scope-select"
            className="mt-1 w-full rounded-md border px-3 py-2 text-sm bg-white"
            style={{ borderColor: "var(--pxos-border)" }}
          >
            {SCOPE_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          {selected?.hint && (
            <p className="text-xs opacity-70 mt-1.5" data-testid="privacy-export-scope-hint">{selected.hint}</p>
          )}
        </div>
      </div>

      <div className="border-t pt-4 space-y-3" style={{ borderColor: "var(--pxos-border)" }}>
        <div className="flex items-center gap-2 text-xs font-medium opacity-80">
          <KeyRound size={14} /> Password richiesta (L2 stepup)
        </div>
        <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
          <input
            type="password" value={pwd} onChange={(e) => setPwd(e.target.value)}
            placeholder="La tua password"
            data-testid="privacy-export-password-input"
            className="rounded-md border px-3 py-2 text-sm bg-white"
            style={{ borderColor: "var(--pxos-border)" }}
            autoComplete="current-password"
          />
          <button
            onClick={request} disabled={requesting || !pwd}
            data-testid="privacy-export-request-btn"
            className="pxos-btn-primary inline-flex items-center gap-2 justify-center disabled:opacity-50"
          >
            {requesting ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
            Richiedi export
          </button>
        </div>
      </div>

      <div className="border-t pt-4" style={{ borderColor: "var(--pxos-border)" }}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold opacity-90">I tuoi export</h3>
          <button onClick={load} className="text-xs underline opacity-70 hover:opacity-100"
                  data-testid="privacy-export-refresh-btn">Aggiorna</button>
        </div>
        {loading ? (
          <div className="text-sm opacity-60 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Caricamento…</div>
        ) : items.length === 0 ? (
          <div className="text-sm opacity-60" data-testid="privacy-export-empty">Nessun export finora.</div>
        ) : (
          <ul className="space-y-2" data-testid="privacy-export-list">
            {items.map((it) => {
              const st = STATUS_LABELS[it.status] || { label: it.status, color: "bg-neutral-100" };
              return (
                <li key={it.id} className="rounded-lg border p-3"
                    data-testid={`privacy-export-item-${it.id}`}
                    style={{ borderColor: "var(--pxos-border)" }}>
                  <div className="flex flex-wrap items-center gap-2 justify-between">
                    <div>
                      <div className="text-sm font-medium">
                        {SCOPE_OPTIONS.find(s => s.value === it.scope)?.label || it.scope}
                      </div>
                      <div className="text-xs opacity-70 mt-0.5">
                        Richiesto: {it.created_at ? formatDateTime(it.created_at) : "—"}
                        {it.completed_at && <> · Pronto: {formatDateTime(it.completed_at)}</>}
                        {it.file_size_bytes && <> · {humanBytes(it.file_size_bytes)}</>}
                        {it.expires_at && <> · Scade: {formatDateTime(it.expires_at)}</>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-xs px-2 py-1 rounded-full border ${st.color}`}
                             data-testid={`privacy-export-status-${it.id}`}>
                        {st.label}
                      </span>
                      {it.status === "ready" && it.download_url && (
                        <a
                          href={`${API.replace(/\/api$/, "")}${it.download_url}`}
                          className="pxos-btn-secondary text-xs inline-flex items-center gap-1"
                          data-testid={`privacy-export-download-${it.id}`}
                        >
                          <Download size={12} /> Scarica
                        </a>
                      )}
                      {(it.status === "ready" || it.status === "failed") && (
                        <button
                          onClick={() => remove(it.id)}
                          data-testid={`privacy-export-delete-${it.id}`}
                          className="text-xs text-red-700 hover:underline inline-flex items-center gap-1"
                        >
                          <X size={12} /> Elimina
                        </button>
                      )}
                    </div>
                  </div>
                  {it.status === "failed" && it.error_message && (
                    <p className="text-xs text-red-700 mt-2">Errore: {it.error_message}</p>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// Delete account section
// ═════════════════════════════════════════════════════════════
function DeleteAccountSection() {
  const { refresh } = useApp();
  const [pending, setPending] = useState(null);
  const [loading, setLoading] = useState(true);
  const [graceDays, setGraceDays] = useState(14);
  const [confirmText, setConfirmText] = useState("");
  const [pwd, setPwd] = useState("");
  const [otpMode, setOtpMode] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setPending(await api.gdprGetDeletionStatus()); } catch {}
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const requestOtp = async () => {
    setBusy(true);
    try { await api.stepupOtpRequest(); setOtpSent(true); toast.success("OTP inviato via email"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore invio OTP"); }
    finally { setBusy(false); }
  };

  const scheduleDelete = async () => {
    if (confirmText !== "ELIMINA") {
      toast.error("Digita esattamente 'ELIMINA' per confermare");
      return;
    }
    setBusy(true);
    try {
      if (otpMode) {
        if (!otp) { toast.error("Inserisci l'OTP ricevuto via email"); setBusy(false); return; }
        const r = await api.stepupOtpVerify(otp);
        window.__pxos_stepup = r.stepup_token;
      } else {
        if (!pwd) { toast.error("Inserisci la password"); setBusy(false); return; }
        await ensureStepup(pwd);
      }
      await api.gdprScheduleDeletion({ grace_days: graceDays, confirmation_text: "ELIMINA" });
      toast.success("Cancellazione programmata · controlla la tua email");
      setPwd(""); setOtp(""); setConfirmText(""); setOtpSent(false);
      await load();
      await refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Errore programmazione cancellazione";
      toast.error(detail);
    } finally { setBusy(false); }
  };

  const cancelPending = async () => {
    if (!pending?.cancellation_token) return;
    setBusy(true);
    try {
      await api.gdprCancelDeletion(pending.cancellation_token);
      toast.success("Cancellazione annullata · il tuo account è di nuovo attivo");
      setPending({ pending: false });
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore annullamento");
    } finally { setBusy(false); }
  };

  const daysUntil = useMemo(() => {
    if (!pending?.scheduled_deletion_at) return null;
    const ms = new Date(pending.scheduled_deletion_at).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
  }, [pending]);

  return (
    <div
      className="rounded-2xl border-2 p-5 sm:p-6 space-y-5"
      data-testid="privacy-delete-section"
      style={{ borderColor: "rgb(220, 38, 38)", background: "rgba(254, 242, 242, 0.5)" }}
    >
      <div className="flex items-start gap-3">
        <ShieldAlert size={22} className="mt-1 text-red-700" />
        <div>
          <h2 className="font-display text-xl font-semibold text-red-800" data-testid="privacy-delete-heading">
            Elimina il mio account
          </h2>
          <p className="text-sm text-red-900/80 mt-1">
            Questa azione programma la cancellazione <strong>definitiva</strong> di
            tutti i tuoi dati. Hai un periodo di grazia per cambiare idea.
            Al termine, tutti i dati (cicli, finanze, documenti, CRM, memorie IA) verranno rimossi.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="text-sm opacity-60 flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Caricamento…
        </div>
      ) : pending?.pending ? (
        <div className="rounded-lg bg-white border-2 border-red-300 p-4 space-y-3"
             data-testid="privacy-delete-pending-box">
          <div className="flex items-center gap-2">
            <Clock size={18} className="text-red-700" />
            <div className="font-semibold text-red-800">Cancellazione già programmata</div>
          </div>
          <ul className="text-sm space-y-1 pl-6">
            <li>Programmata il: <strong>{formatDateTime(pending.created_at)}</strong></li>
            <li>Verrà eseguita: <strong>{formatDateTime(pending.scheduled_deletion_at)}</strong></li>
            <li>Giorni rimanenti: <strong data-testid="privacy-delete-days-remaining">{daysUntil}</strong></li>
          </ul>
          <p className="text-xs opacity-70">
            Se hai cambiato idea puoi annullare ora. Nessun dato è ancora stato rimosso.
          </p>
          <button
            onClick={cancelPending} disabled={busy}
            data-testid="privacy-delete-cancel-btn"
            className="pxos-btn-primary inline-flex items-center gap-2 disabled:opacity-50"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
            Annulla la cancellazione
          </button>
        </div>
      ) : (
        <>
          <div className="rounded-lg bg-white p-4 space-y-4 border border-red-200">
            <div>
              <label className="text-xs font-medium opacity-80">
                Periodo di grazia: <strong data-testid="privacy-delete-grace-value">{graceDays} giorni</strong>
              </label>
              <input
                type="range" min={7} max={30} value={graceDays}
                onChange={(e) => setGraceDays(parseInt(e.target.value, 10))}
                data-testid="privacy-delete-grace-slider"
                className="w-full mt-2 accent-red-600"
              />
              <div className="flex justify-between text-[10px] opacity-60 mt-0.5">
                <span>7 giorni</span><span>14 (default)</span><span>30 giorni</span>
              </div>
            </div>

            <div>
              <label className="text-xs font-medium opacity-80">
                Digita <strong>ELIMINA</strong> per confermare
              </label>
              <input
                type="text" value={confirmText}
                onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                placeholder="ELIMINA"
                data-testid="privacy-delete-confirm-input"
                className="mt-1 w-full rounded-md border-2 px-3 py-2 text-sm bg-white font-mono uppercase"
                style={{ borderColor: confirmText === "ELIMINA" ? "rgb(220, 38, 38)" : "var(--pxos-border)" }}
                autoComplete="off"
              />
            </div>

            <div className="border-t pt-3" style={{ borderColor: "var(--pxos-border)" }}>
              <div className="flex items-center gap-3 text-xs font-medium mb-2">
                <button
                  onClick={() => setOtpMode(false)}
                  data-testid="privacy-delete-mode-pwd"
                  className={`px-3 py-1 rounded-full ${!otpMode ? "bg-red-600 text-white" : "bg-neutral-100"}`}
                >Password</button>
                <button
                  onClick={() => setOtpMode(true)}
                  data-testid="privacy-delete-mode-otp"
                  className={`px-3 py-1 rounded-full ${otpMode ? "bg-red-600 text-white" : "bg-neutral-100"}`}
                >OTP email</button>
              </div>
              {!otpMode ? (
                <input
                  type="password" value={pwd} onChange={(e) => setPwd(e.target.value)}
                  placeholder="La tua password"
                  data-testid="privacy-delete-password-input"
                  className="w-full rounded-md border px-3 py-2 text-sm bg-white"
                  style={{ borderColor: "var(--pxos-border)" }}
                  autoComplete="current-password"
                />
              ) : (
                <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
                  <input
                    type="text" inputMode="numeric" pattern="\d*" maxLength={6}
                    value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="Codice a 6 cifre"
                    data-testid="privacy-delete-otp-input"
                    className="rounded-md border px-3 py-2 text-sm bg-white font-mono tracking-widest text-center"
                    style={{ borderColor: "var(--pxos-border)" }}
                    disabled={!otpSent}
                  />
                  <button
                    onClick={requestOtp} disabled={busy || otpSent}
                    data-testid="privacy-delete-otp-request-btn"
                    className="pxos-btn-secondary text-xs inline-flex items-center gap-1 disabled:opacity-50"
                  >
                    <MailCheck size={12} />
                    {otpSent ? "OTP inviato" : "Ricevi OTP"}
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 justify-between">
            <div className="text-xs text-red-900/80 flex items-center gap-2">
              <AlertTriangle size={12} />
              Verrai disconnesso da tutti i device e il login sarà bloccato durante il periodo di grazia.
            </div>
            <button
              onClick={scheduleDelete}
              disabled={busy || confirmText !== "ELIMINA" || (!pwd && !otpMode) || (otpMode && !otp)}
              data-testid="privacy-delete-schedule-btn"
              className="inline-flex items-center gap-2 rounded-full bg-red-600 text-white px-5 py-2 text-sm font-semibold hover:bg-red-700 disabled:opacity-50"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              Programma cancellazione
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// Page
// ═════════════════════════════════════════════════════════════
export default function PrivacyData() {
  return (
    <div className="space-y-6 max-w-3xl" data-testid="privacy-data-page">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-semibold">Privacy e dati</h1>
        <p className="text-sm opacity-75 mt-1 max-w-2xl">
          Esporta una copia dei tuoi dati (GDPR art. 20 · portabilità) o chiedi
          la cancellazione dell'account (art. 17 · diritto all'oblio).
          Le operazioni sensibili richiedono conferma con password o OTP.
        </p>
      </div>
      <ExportSection />
      <DeleteAccountSection />
    </div>
  );
}
